// Zustand store powering the `RoutinePickerDialog` overload preview.
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 9 (routine) — overload
// preview dialog follow-up.
//
// `definitionProvider.handleProvideDefinition` runs in a Monaco closure (no
// React tree), so it can't render a dialog directly. Instead, the dispatcher
// pushes the candidate set into this store via `openPicker(...)`; the
// React-tree-mounted `RoutinePickerDialog` reads the state and renders the
// list. Selecting a candidate calls `navigateToRoutine` and closes the
// dialog. Cancellation closes without navigating.
//
// Mirrors the shape of `connectionInfoDialogStore` — the established pattern
// for "imperatively-triggered, React-rendered" dialogs in this app.

import { create } from 'zustand';
import type { DatabaseEngineId } from '@/core/engines';
import type { RoutineBindingHit } from './routineAtCursor';

export type RoutinePickerCandidate = RoutineBindingHit;

export type RoutinePickerContext = {
  readonly engineId: DatabaseEngineId;
  readonly connectionId: string;
  readonly database?: string;
};

type RoutinePickerState = {
  readonly isOpen: boolean;
  readonly candidates: readonly RoutinePickerCandidate[];
  readonly context: RoutinePickerContext | null;
};

type RoutinePickerActions = {
  /** Open the picker with a candidate list. The dispatcher should only call
   *  this when `candidates.length >= 2`; the React component renders nothing
   *  for 0/1 candidates anyway, but the dispatcher avoids the no-op for
   *  performance and clarity. */
  readonly openPicker: (
    candidates: readonly RoutinePickerCandidate[],
    context: RoutinePickerContext,
  ) => void;
  /** Close the picker (cancel). */
  readonly closePicker: () => void;
};

const initialState: RoutinePickerState = {
  isOpen: false,
  candidates: [],
  context: null,
};

export const useRoutinePickerStore = create<RoutinePickerState & RoutinePickerActions>((set) => ({
  ...initialState,

  openPicker: (candidates, context) =>
    set({
      isOpen: true,
      candidates,
      context,
    }),

  closePicker: () => set(initialState),
}));
